// The persona-assist job card (decisions 6, 10, 12, 13): running · interrotto · failed (our reason, optional
// Riprova) · completed + review ("Apri la proposta"). A start refusal sits above it: billing refusals through
// RunRefusalNotice, a 409 PERSONA_JOB_ACTIVE as "already in progress" (info, never an error), anything else as
// OUR sentence. ⛔ Never the server's text: the job's error is worded from its CODE only.
// v198 — the start refusal block is exported as `StartNotice`: the modal's brief / instructions steps show it too.
import React from 'react';
import { useTranslation } from 'react-i18next';
import RunRefusalNotice from '../../../components/credits/RunRefusalNotice.jsx';
import { refusalFromError } from '../../../utils/runRefusal.js';
import { SEMANTIC } from '../../../constants/theme.js';
import { alertBox, ghostBtn, primaryBtn } from '../personaStyles.js';
import { assistErrorCopy, jobErrorCopy } from './assistCopy.js';
import DocResults from './DocResults.jsx';

const actions = { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' };

/** The start refusal block — shared by the page's JobCard and the modal's brief / instructions steps (v198). */
export function StartNotice({ startError, onDismiss }) {
  const { t } = useTranslation('context');
  if (!startError) return null;
  const refusal = refusalFromError(startError);
  if (refusal) {
    return (
      <div style={{ marginBottom: '16px' }}>
        <RunRefusalNotice refusal={refusal} onDismiss={onDismiss} />
      </div>
    );
  }
  if (startError.code === 'PERSONA_JOB_ACTIVE') {
    return <div role="status" style={alertBox(SEMANTIC.info.fg)}>{t('personas.assist.busyElsewhere')}</div>;
  }
  return <div role="alert" style={alertBox(SEMANTIC.errore.fg)}>{assistErrorCopy(t, startError)}</div>;
}

function runningLine(t, job) {
  if (job.kind === 'create' && job.label) return t('personas.assist.jobRunningCreate', { name: job.label });
  if (job.kind === 'personalize' && job.persona_id) {
    return t('personas.assist.jobRunningPersonalize', { id: job.persona_id });
  }
  return null;
}

function JobBody({ t, job, view, review, onOpen, onDismiss, onRetry }) {
  if (view === 'running') {
    const head = runningLine(t, job);
    return (
      <div role="status" style={alertBox(SEMANTIC.info.fg)}>
        {head && <div style={{ fontWeight: 600, marginBottom: '4px' }}>{head}</div>}
        <div>{t('personas.assist.jobRunning')}</div>
      </div>
    );
  }
  // No Ignora on a stale job: it is still an ACTIVE job, which dismiss() never drops.
  if (view === 'stale') {
    return <div role="status" style={alertBox(SEMANTIC.attenzione.fg)}>{t('personas.assist.jobStale')}</div>;
  }
  if (view === 'failed') {
    // A completed job with no proposal is read as failed (edge case) — worded as an internal error.
    const code = job.error?.code || (job.status === 'completed' ? 'INTERNAL_ERROR' : null);
    return (
      <div role="alert" style={alertBox(SEMANTIC.errore.fg)}>
        <div>{t('personas.assist.jobFailed', { reason: jobErrorCopy(t, code) })}</div>
        <div style={actions}>
          {onRetry && (
            <button type="button" style={primaryBtn(false)} onClick={onRetry}>{t('personas.assist.retry')}</button>
          )}
          <button type="button" style={ghostBtn(false)} onClick={onDismiss}>{t('personas.assist.dismiss')}</button>
        </div>
      </div>
    );
  }
  if (view === 'completed' && review) {
    return (
      <div role="status" style={alertBox(SEMANTIC.verde.fg)}>
        <div style={{ fontWeight: 600 }}>{t('personas.assist.jobDone')}</div>
        {job.kb_used === false && <div style={{ marginTop: '6px' }}>{t('personas.assist.kbUnused')}</div>}
        <DocResults documents={job.documents} />
        <div style={actions}>
          <button type="button" style={primaryBtn(false)} onClick={() => onOpen && onOpen(job)}>
            {t('personas.assist.openProposal')}
          </button>
          <button type="button" style={ghostBtn(false)} onClick={onDismiss}>{t('personas.assist.dismiss')}</button>
        </div>
      </div>
    );
  }
  return null;
}

export default function JobCard({ job, view, review, startError, onOpen, onDismiss, onRetry }) {
  const { t } = useTranslation('context');
  const shown = Boolean(job) && (['running', 'stale', 'failed'].includes(view) || (view === 'completed' && review));
  if (!startError && !shown) return null;
  return (
    <div data-testid="persona-job-card">
      <StartNotice startError={startError} onDismiss={onDismiss} />
      {shown && (
        <JobBody t={t} job={job} view={view} review={review} onOpen={onOpen} onDismiss={onDismiss} onRetry={onRetry} />
      )}
    </div>
  );
}
